import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import {
  Block as BlockIcon,
  Gavel as GavelIcon,
  AccountBalance as BankIcon,
  NotificationsActive as ReminderIcon,
  Replay as ReplayIcon,
} from "@mui/icons-material";
import { withTranslation } from "react-i18next";
import axios from "axios";
import { useUserStore } from "../../store";

/**
 * Confirmation for closing Amili Kassavirta (Asetukset > Verkkolaskutus > Maksuvalvonta).
 *
 * The dialog spells out what changes before the admin confirms: new invoices stop going to
 * Amili and are sent with the workshop's own bank details, the invoices Amili already holds
 * stay with Amili (reminders and collection continue, and Verstaani keeps showing them), and
 * Verstaani's own payment reminders become available again.
 */
function AmiliDeactivationDialog({ open, onClose, onSuccess, t }) {
  const { currentOrganization } = useUserStore();
  const organizationId = currentOrganization?.organization_id;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [openAssignments, setOpenAssignments] = useState(null);

  useEffect(() => {
    if (!open || !organizationId) return;
    setError(null);
    setOpenAssignments(null);
    let cancelled = false;
    axios
      .get(`/api/organizations/${organizationId}/amili/status/`)
      .then((response) => {
        if (!cancelled) setOpenAssignments(response.data.open_assignments ?? 0);
      })
      .catch(() => {
        if (!cancelled) setOpenAssignments(0);
      });
    return () => {
      cancelled = true;
    };
  }, [open, organizationId]);

  const handleDeactivate = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.post(`/api/organizations/${organizationId}/amili/deactivate/`);
      if (onSuccess) await onSuccess(response.data);
      onClose();
    } catch (err) {
      setError(
        err.response?.data?.error ||
          err.response?.data?.detail ||
          t("amili.deactivateFailed", "Maksuvalvonnan lopettaminen epäonnistui")
      );
    } finally {
      setLoading(false);
    }
  };

  const consequences = [
    {
      icon: <BlockIcon color="action" />,
      text: t(
        "amili.deactivateNewInvoices",
        "Uudet laskut eivät enää mene Amilin maksuvalvontaan. Ne lähetetään omalla tilinumerollasi ja viitteelläsi, ja merkitset niiden maksut itse."
      ),
    },
    {
      icon: <GavelIcon color="action" />,
      text:
        openAssignments === null
          ? t(
              "amili.deactivateOpenInvoices",
              "Laskut, jotka ovat jo Amilin maksuvalvonnassa tai perinnässä, jäävät Amilille. Amili jatkaa niiden muistutuksia ja perintää normaalisti, ja niiden tapahtumat ja maksut näkyvät edelleen Verstaanissa."
            )
          : t("amili.deactivateOpenInvoicesCount", {
              count: openAssignments,
              defaultValue:
                "{{count}} laskua on nyt Amilin maksuvalvonnassa tai perinnässä. Ne jäävät Amilille: Amili jatkaa niiden muistutuksia ja perintää normaalisti, ja niiden tapahtumat ja maksut näkyvät edelleen Verstaanissa.",
            }),
    },
    {
      icon: <BankIcon color="action" />,
      text: t(
        "amili.deactivateMaventaStays",
        "Verkkolaskutus (Maventa) pysyy käytössä. Vain maksuvalvonta, muistutukset ja perintä päättyvät."
      ),
    },
    {
      icon: <ReminderIcon color="action" />,
      text: t(
        "amili.deactivateOwnReminders",
        "Verstaanin oma maksumuistutus yritysasiakkaiden erääntyneille laskuille tulee käyttöön."
      ),
    },
    {
      icon: <ReplayIcon color="action" />,
      text: t(
        "amili.deactivateReactivate",
        "Maksuvalvonnan voi ottaa myöhemmin uudelleen käyttöön samasta paikasta. Aktivointi tehdään silloin alusta, tunnistautuminen mukaan lukien."
      ),
    },
  ];

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="sm" fullWidth data-testid="amili-deactivate-dialog">
      <DialogTitle>{t("amili.deactivateTitle", "Lopeta maksuvalvonta")}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 1 }}>
          {t(
            "amili.deactivateIntro",
            "Amili Kassavirta -palvelu suljetaan tältä organisaatiolta. Tämä tapahtuu sen jälkeen:"
          )}
        </Typography>
        <List dense disablePadding>
          {consequences.map((item, idx) => (
            <ListItem key={idx} alignItems="flex-start" disableGutters>
              <ListItemIcon sx={{ minWidth: 36, mt: 0.5 }}>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} primaryTypographyProps={{ variant: "body2" }} />
            </ListItem>
          ))}
        </List>
        {openAssignments > 0 && (
          <Alert severity="info" sx={{ mt: 1 }} data-testid="amili-deactivate-open-note">
            {t(
              "amili.deactivateOpenNote",
              "Näet Amilille jäävät laskut Laskut-listan pikasuodattimilla Muistutus lähetetty ja Perinnässä."
            )}
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          {t("Cancel")}
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleDeactivate}
          disabled={loading}
          data-testid="amili-deactivate-confirm"
        >
          {loading ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={18} color="inherit" />
              {t("amili.deactivating", "Lopetetaan...")}
            </Box>
          ) : (
            t("amili.deactivateConfirm", "Lopeta maksuvalvonta")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default withTranslation()(AmiliDeactivationDialog);
